import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, default as axiosBase } from 'axios';
import { toast } from 'react-toastify';

/**
 * API base URL resolution.
 * - If VITE_API_BASE_URL is provided, use it.
 * - Otherwise use same-origin '/api' so Vite proxy can route to backend in local dev.
 */
function resolveApiBaseUrl(): string {
  const env = import.meta.env.VITE_API_BASE_URL;
  if (typeof env === 'string' && env.trim().length > 0) {
    return env.trim();
  }
  return '/api';
}

const API_BASE_URL = resolveApiBaseUrl();

/**
 * Create axios instance with default configuration
 */
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor - Add auth token to requests
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token =
      localStorage.getItem('accessToken') || localStorage.getItem('token');
    const url = config.url || '';
    const isPublicAuth =
      url.includes('/login') ||
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/forgot-password') ||
      url.includes('/auth/reset-password');

    if (token && config.headers && !isPublicAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

function drainQueue(newToken: string) {
  refreshQueue.forEach((cb) => cb(newToken));
  refreshQueue = [];
}

/**
 * Response interceptor — auto-refresh expired access token, then retry.
 */
api.interceptors.response.use(
  (response) => {
    console.log('API Response Interceptor - Success:', response.config.url, response.status);
    return response;
  },
  async (error: AxiosError<any>) => {
    console.error('API Response Interceptor - Error:', error.config?.url, error.response?.status, error.message);
    console.error('API Response Interceptor - Error details:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const is401 = error.response?.status === 401;
    const isLoginUrl = originalRequest.url?.includes('/login');
    const isRefreshUrl = originalRequest.url?.includes('/auth/refresh');

    // ── Auto-refresh flow ────────────────────────────────────────────────
    if (is401 && !isLoginUrl && !isRefreshUrl && !originalRequest._retry) {
      const refreshToken =
        localStorage.getItem('refreshToken') || localStorage.getItem('refresh_token');

      if (refreshToken) {
        originalRequest._retry = true;

        if (isRefreshing) {
          // Queue while another refresh is in flight
          return new Promise((resolve) => {
            refreshQueue.push((token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(api(originalRequest));
            });
          });
        }

        isRefreshing = true;
        try {
          const res = await axios.post(`${API_BASE_URL}/v1/auth/refresh`, { refreshToken });
          const newAccessToken: string = res.data?.data?.accessToken;
          if (newAccessToken) {
            localStorage.setItem('accessToken', newAccessToken);
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            }
            drainQueue(newAccessToken);
            return api(originalRequest);
          }
        } catch {
          // Refresh failed — clear auth and redirect to login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      } else {
        // No refresh token — send to login
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }

    // Non-401 errors: show toast (except for login page)
    const errorMessage = error.response?.data?.message || 'An error occurred';
    if (!is401 && !isLoginUrl) {
      toast.error(errorMessage);
    }

    return Promise.reject(error);
  }
);

export default api;


